/**
 * Input - Reusable form input component with validation states.
 *
 * Features:
 * - Label support
 * - Error state with message
 * - Helper text
 * - Left/right icons
 */

import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  warning?: boolean;
  helperText?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      warning,
      helperText,
      leftIcon,
      rightIcon,
      fullWidth = true,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    // Base input classes
    const baseClasses =
      'px-4 py-3 rounded-2xl bg-white/5 border text-white placeholder-text-muted focus:outline-none focus:ring-1 transition-all duration-200';

    // State classes
    const stateClasses = error
      ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/30'
      : warning
        ? 'border-orange-400/50 focus:border-orange-400/70 focus:ring-orange-400/30'
        : 'border-white/10 focus:border-accent/50 focus:ring-accent/30';

    // Width classes
    const widthClasses = fullWidth ? 'w-full' : '';

    // Padding adjustments for icons
    const paddingClasses = `${leftIcon ? 'pl-10' : ''} ${rightIcon ? 'pr-10' : ''}`;

    return (
      <div className={`${fullWidth ? 'w-full' : ''}`}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-text-muted mb-2"
          >
            {label}
          </label>
        )}

        {/* Input wrapper */}
        <div className="relative">
          {/* Left icon */}
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">
              {leftIcon}
            </div>
          )}

          {/* Input */}
          <input
            ref={ref}
            id={inputId}
            className={`${baseClasses} ${stateClasses} ${widthClasses} ${paddingClasses} ${className}`}
            {...props}
          />

          {/* Right icon */}
          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary">
              {rightIcon}
            </div>
          )}
        </div>

        {/* Error message */}
        {error && <p className="mt-1.5 text-sm text-red-400">{error}</p>}

        {/* Helper text */}
        {helperText && !error && (
          <p className={`mt-1.5 text-sm ${warning ? 'text-orange-400' : 'text-text-secondary'}`}>{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
